import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
    Button,
    TextInput,
    NumberInput,
    ActionIcon,
    Checkbox,
} from '@mantine/core';
import {
    IconUsers,
    IconTrash,
    IconExternalLink,
    IconPlus,
    IconMinus,
    IconBabyCarriage,
} from '@tabler/icons-react';
import { ReservationPaxGroup, Person, EMPTY_PERSON, PaxCategory, PAX_CATEGORIES, PAX_CATEGORY_COLORS, ServiceBooking } from '@turizi/shared';
import CardModal from '../card-modal';
import { PERSONS_PATH } from '@/lib/navigation';
import { useReservation } from '@/hooks/api-hooks';
import CreateOrSelectPerson from '../create-or-select-person-component';
import { ShowPerson } from '../snippets';

interface PaxGroupModalProps {
    opened: boolean;
    onClose: () => void;
    reservationId: number | string;
    group?: ReservationPaxGroup | null;
    onSave: (data: Partial<ReservationPaxGroup>, addToAllServices?: boolean) => Promise<void>;
    onDelete?: (groupId: number) => Promise<void>;
    isLoading?: boolean;
}

export type HeadcountCategory = 'adults' | 'children' | 'seniors' | 'guests' | 'infants';

interface HeadcountCategoryConfig {
    key: HeadcountCategory;
    labelKey: string;
}

const HEADCOUNT_CATEGORIES: HeadcountCategoryConfig[] = [
    { key: 'adults', labelKey: 'adults' },
    { key: 'children', labelKey: 'children' },
    { key: 'seniors', labelKey: 'seniors' },
    { key: 'infants', labelKey: 'infants' },
    { key: 'guests', labelKey: 'guests' },
];

export default function PaxGroupModal({
    opened,
    onClose,
    reservationId,
    group,
    onSave,
    onDelete,
    isLoading = false,
}: PaxGroupModalProps) {
    const { t } = useTranslation();
    const {
        data: resData
    } = useReservation(reservationId);
    // const {
    //     data: allPaxGroups = []
    // } = useReservationPaxGroups(reservationId);

    const [paxsList, setPaxsList] = useState<Person[]>([]);
    const [name, setName] = useState('');

    const [adults, setAdults] = useState<number | string>('');
    const [children, setChildren] = useState<number | string>('');
    const [infants, setInfants] = useState<number | string>('');
    const [seniors, setSeniors] = useState<number | string>('');
    const [guests, setGuests] = useState<number | string>('');
    const [notes, setNotes] = useState('');
    const [addToAllBookings, setAddToAllBookings] = useState(false);

    const isEdit = Boolean(group?.id);

    // Populate when opening
    useEffect(() => {
        if (opened) {
            setAddToAllBookings(false);
            if (group) {
                let initialItems: Person[] = [];
                initialItems = group.persons?.map(p => p) || [];
                setPaxsList(initialItems);
                setName(group.name || '');

                setAdults(group.adults || '');
                setChildren(group.children || '');
                setInfants(group.infants || '');
                setSeniors(group.seniors || '');
                setGuests(group.guests || '');
                setNotes(group.observations || '');
            } else {
                setPaxsList([]);
                setName('');
                setAdults('');
                setChildren('');
                setInfants('');
                setSeniors('');
                setGuests('');
                setNotes('');
            }
        }
    }, [opened, group, resData?.data?.persons]);

    const seatPaxs = (Number(adults) || 0) + (Number(children) || 0) + (Number(seniors) || 0) + (Number(guests) || 0);
    const hasBookings = (resData?.data?.service_bookings?.length || 0) > 0;

    // Add person to items and sync state
    const addPersonItem = (pax: Person) => {
        if (pax.id && paxsList.some((p) => p.id === pax.id)) return;
        setPaxsList([...paxsList, pax]);
    };

    // Remove passenger from group
    const handleRemovePax = (indexToRemove: number) => {
        const itemToRemove = paxsList[indexToRemove];
        if (!itemToRemove) return;

        const newItems = paxsList.filter((_, i) => i !== indexToRemove);
        setPaxsList(newItems);
    };

    // Submit / Save
    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();

        const finalAdults = Number(adults) || 0;
        const finalChildren = Number(children) || 0;
        const finalInfants = Number(infants) || 0;
        const finalSeniors = Number(seniors) || 0;
        const finalGuests = Number(guests) || 0;
        const resolvedName = name.trim();

        const uniquePersonIds = Array.from(
            new Set((paxsList || []).map((p) => Number(p.id)).filter(Boolean))
        );

        const personIds = uniquePersonIds;

        const members = uniquePersonIds
            .map(pid => ({
                pax_group_id: group?.id,
                person_id: pid,
            }));

        const payload: Partial<ReservationPaxGroup> = {
            name: resolvedName,
            adults: finalAdults,
            children: finalChildren,
            infants: finalInfants,
            seniors: finalSeniors,
            guests: finalGuests,
            person_ids: personIds,
            members,
        };

        await onSave(payload, !isEdit && addToAllBookings);
    };

    const headcountValues: Record<HeadcountCategory, number | string> = {
        adults,
        children,
        seniors,
        guests,
        infants,
    };

    const setHeadcountValue = (category: HeadcountCategory, val: number | string) => {
        const num = Number(val);
        const nextVal: number | string = val === '' || isNaN(num) || num <= 0 ? '' : num;
        if (category === 'adults') setAdults(nextVal);
        else if (category === 'children') setChildren(nextVal);
        else if (category === 'seniors') setSeniors(nextVal);
        else if (category === 'guests') setGuests(nextVal);
        else if (category === 'infants') setInfants(nextVal);
    };

    const renderHeadcountRow = (key: HeadcountCategory, labelKey: string) => {
        const value = headcountValues[key];
        const numericValue = Number(value) || 0;
        const colors = PAX_CATEGORY_COLORS[key];
        return (
            <div
                key={key}
                className="flex items-center justify-between px-3 py-1 bg-gray-50 rounded-md"
            >
                <span className={`text-xs font-medium ${colors?.text || 'text-slate-700'}`}>{t(labelKey)}</span>
                <div className="flex items-center gap-1">
                    <ActionIcon
                        size="md"
                        radius="xl"
                        variant="light"
                        color="black"
                        onClick={() => setHeadcountValue(key, numericValue - 1)}
                        disabled={numericValue <= 0}
                    >
                        <IconMinus size={11} stroke={3} />
                    </ActionIcon>
                    <NumberInput
                        value={value}
                        onChange={(val) => setHeadcountValue(key, val)}
                        min={0}
                        hideControls
                        size="sm"
                        className="w-15 font-bold"
                        styles={{ input: { textAlign: 'center' } }}
                    />
                    <ActionIcon
                        size="md"
                        radius="xl"
                        variant="light"
                        color="black"
                        onClick={() => setHeadcountValue(key, numericValue + 1)}
                    >
                        <IconPlus size={11} stroke={3} />
                    </ActionIcon>
                </div>
            </div>
        );
    };

    return (
        <CardModal
            opened={opened}
            onClose={onClose}
            size="90%"
            header={
                <div className="flex items-center justify-between w-full pr-4">
                    <div className="flex items-center gap-2">
                        <IconUsers size={20} className="text-slate-700" />
                        <span className="font-bold text-base text-slate-800">
                            {isEdit ? t('edit_pax_group') : t('add_pax_group')}
                        </span>
                    </div>
                </div>
            }
            footer={
                <div className="w-full flex items-center justify-between">
                    <div>
                        {isEdit && onDelete && group?.id && (
                            <Button
                                color="red"
                                variant="subtle"
                                size="xs"
                                leftSection={<IconTrash size={14} />}
                                onClick={() => group.id && onDelete(group.id)}
                                disabled={isLoading}
                            >
                                {t('delete')}
                            </Button>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="subtle"
                            color="black"
                            size="xs"
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            color="black"
                            size="xs"
                            onClick={() => handleSubmit()}
                            loading={isLoading}
                        >
                            {t('save_group')}
                        </Button>
                    </div>
                </div>
            }
        >
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">

                {/*  Column: Group Name, Numbers Breakdown & Notes */}
                <div className="md:col-span-4 space-y-4">
                    {/* 1. Group Name */}
                    <div className="space-y-1">
                        <TextInput
                            label={t('pax_group_name')}
                            value={name}
                            onChange={(e) => setName(e.currentTarget.value)}
                            size="sm"
                            className='font-bold'
                        />
                    </div>

                    {/* 2. Headcount Numbers Breakdown - Single column: Diff Paxs vs Infants */}
                    <div className="space-y-3 border-t border-slate-200 pt-3">
                        {/* Paxs / Seats */}
                        <div className="space-y-1.5">
                            {HEADCOUNT_CATEGORIES.map(({ key, labelKey }) => renderHeadcountRow(key, labelKey))}
                        </div>

                        {/* Total Summary Row */}
                        <div className='flex gap-2 justify-center items-center'>
                            <div className='flex items-center gap-1 solid-box-border-orange'>
                                <IconUsers size={14} className="text-slate-400" />
                                <div className='text-lg font-bold'>
                                    {seatPaxs}
                                </div>
                            </div>
                            <div className='flex items-center gap-1 solid-box-border-orange'>
                                <IconBabyCarriage size={14} className="text-slate-400" />
                                <div className='text-lg font-bold'>{Number(infants) || 0}</div>
                            </div>
                        </div>
                    </div>

                    {/* 3. Assign to all bookings option when creating */}
                    {!isEdit && hasBookings && (
                        <div className="pt-2 border-t border-slate-200">
                            <Checkbox
                                label={t('add_to_all_bookings')}
                                checked={addToAllBookings}
                                onChange={(e) => setAddToAllBookings(e.currentTarget.checked)}
                                size="xs"
                                color="dark"
                            />
                        </div>
                    )}
                </div>

                {/*  Column: Paxs list & Add passenger */}
                <div className="md:col-span-8 space-y-3">

                    <div className="text-xs font-semibold text-slate-700">
                        {t('search_or_add_new_passenger')}
                    </div>

                    {/* Quick Add Form */}
                    <CreateOrSelectPerson
                        onSelect={addPersonItem}
                    />

                    <div className="text-xs font-semibold text-slate-700 border-t pt-2 border-slate-200">
                        {t('pax_list')}
                    </div>

                    {/* List of Passengers in this Group */}
                    <div className="space-y-1.5 max-h-[420px] overflow-y-auto subtle-scrollbar pr-0.5">
                        {paxsList?.map((pax, idx) => {
                            return (
                                <div
                                    key={pax.id ? `pax-${pax.id}-${idx}` : `pax-${idx}`}
                                    className="p-2 bg-slate-100 rounded-lg flex items-center justify-between gap-2.5 shadow-2xs hover:border-slate-300 transition-colors"
                                >
                                    {/* Left details */}
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                        <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-600 font-semibold text-xs flex items-center justify-center shrink-0">
                                            {idx + 1}
                                        </div>

                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-1.5">
                                                {pax.id ? (
                                                    <Link
                                                        to={`${PERSONS_PATH}/${pax.id}`}
                                                        target="_blank"
                                                        className="min-w-0 flex items-center gap-1 group"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <ShowPerson
                                                            person={pax}
                                                            hideIcon
                                                            className='font-bold text-blue-500 text-xs group-hover:underline'
                                                        />
                                                        <IconExternalLink size={11} className="text-slate-400 shrink-0" />
                                                    </Link>
                                                ) : (
                                                    <span className="font-semibold text-xs text-slate-900 truncate">
                                                        {pax.name}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-1.5 shrink-0">
                                        <ActionIcon
                                            size="sm"
                                            variant="subtle"
                                            color="gray"
                                            onClick={() => handleRemovePax(idx)}
                                            title={t('delete')}
                                        >
                                            <IconTrash size={14} />
                                        </ActionIcon>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>


            </div>
        </CardModal>
    );
}
